import {
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Typography
} from "@mui/material";
import parsePhoneNumberFromString from "libphonenumber-js";
import { FC, useCallback, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useMutation } from "react-query";
import { EmployerSource } from "../../models";
import { useConfig } from "../../utils/useConfig";
import { EmployerForm, EmployerFormValuesType } from "../shared/EmployerForm";

export const OnboardingForm: FC<{ setSignupComplete: () => void }> = ({
  setSignupComplete
}) => {
  const { config } = useConfig();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isAcceptedTCs, setIsAcceptedTCs] = useState(false);
  const [error, setError] = useState("");

  const createEmployerMutation = useMutation(
    async ({
      formValues,
      recaptchaToken
    }: {
      formValues: EmployerFormValuesType;
      recaptchaToken: string | undefined;
    }) => {
      if (!isAcceptedTCs)
        throw new Error("Please accept our Terms and Conditions to proceed.");

      if (!recaptchaToken) throw new Error("ReCaptcha Error");

      const formattedPhoneNumber = parsePhoneNumberFromString(
        formValues.contact.phone,
        formValues.contact.address.country
      )?.number;

      const response = await fetch(`${config?.API_URL}/employers`, {
        method: "POST",
        headers: {
          "recaptcha-token": recaptchaToken,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formValues,
          contact: {
            ...formValues.contact,
            phone: formattedPhoneNumber,
            address: {
              ...formValues.contact.address,
              city: formValues.contact.address.city,
              // Only include state/province for outside of NZ
              stateOrProvince:
                formValues.contact.address.country !== "NZ"
                  ? formValues.contact.address.stateOrProvince
                  : undefined
            }
          },
          source: EmployerSource.SELF_SERVICE
        })
      });

      if (!response.ok) {
        let responseJson;
        try {
          responseJson = await response.json();
        } catch (err) {
          throw new Error("There was a problem signing up.");
        }

        switch (response.status) {
          case 409:
            throw new Error("Email already in use. Please contact support");
          case 400:
            if (responseJson.errors["Contact.Phone"]) {
              throw new Error(responseJson.errors["Contact.Phone"][0]);
            } else if (responseJson.errors["Contact.Email"]) {
              throw new Error(responseJson.errors["Contact.Email"][0]);
            } else {
              throw new Error("There was a problem signing up.");
            }
          default:
            throw new Error("There was a problem signing up.");
        }
      }
    },
    {
      onSuccess: () => {
        setSignupComplete();
      },
      onError: (error: Error) => {
        console.error(error.message);
        setError(error.message);
      }
    }
  );

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }

    return await executeRecaptcha("onboarding");
  }, [executeRecaptcha]);

  return (
    <EmployerForm
      isCreateEmployer={true}
      isLoading={createEmployerMutation.isLoading}
      onSubmit={async (formValues: EmployerFormValuesType) => {
        const recaptchaToken = await handleReCaptchaVerify();
        createEmployerMutation.mutate({ formValues, recaptchaToken });
      }}
    >
      <Grid container spacing={2} my={1}>
        {error && (
          <Grid item xs={12}>
            <Typography color="error">{error}</Typography>
          </Grid>
        )}
      </Grid>

      <FormControlLabel
        control={
          <Checkbox
            required
            onChange={(e) => {
              setIsAcceptedTCs(e.target.checked);
            }}
          />
        }
        label={
          <span>
            I accept the{" "}
            <Link href="https://example.com/terms" target="_blank">
              terms and conditions
            </Link>
          </span>
        }
      />
    </EmployerForm>
  );
};
